import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  Search,
  X,
  Plus,
  Save,
  ChevronLeft,
  Building2,
  Star,
  Edit3,
  Globe,
  MapPin,
  ChevronDown,
} from "lucide-react";
import { db, storage, auth } from "../firebase";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import ilceKoyler from "../json/ilce-koyler.json";
import ilIlceData from "../json/il-ilceler.json";
import ulkeSehirData from "../json/ulke-sehir.json";
import ProductsSection, {
  uploadAndPrepareProducts,
} from "../components/ProductsSection";

import { SEKTOR_DATA, ANA_SEKTORLER } from "../hooks/sektorData";

const COUNTRIES = (() => {
  const uniqueCountries = [...new Set(ulkeSehirData.map((item) => item.ulke))];
  const others = uniqueCountries
    .filter((c) => c !== "Türkiye")
    .sort((a, b) => a.localeCompare(b, "tr"));
  return ["Türkiye", ...others];
})();

const PRIORITY_CITIES = ["İstanbul", "Sivas", "Ankara", "İzmir"];

const TURKEY_CITIES = (() => {
  const allCities = ilIlceData.map((il) => il.text);

  const priority = allCities
    .filter((city) =>
      PRIORITY_CITIES.some(
        (p) => p.toLocaleLowerCase("tr") === city.toLocaleLowerCase("tr")
      )
    )
    .sort((a, b) => {
      const aIndex = PRIORITY_CITIES.findIndex(
        (p) => p.toLocaleLowerCase("tr") === a.toLocaleLowerCase("tr")
      );
      const bIndex = PRIORITY_CITIES.findIndex(
        (p) => p.toLocaleLowerCase("tr") === b.toLocaleLowerCase("tr")
      );
      return aIndex - bIndex;
    });

  const others = allCities
    .filter((city) => !priority.includes(city))
    .sort((a, b) => a.localeCompare(b, "tr"));

  return [...priority, ...others];
})();
const toTitleTR = (s = "") =>
  s
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) => (w ? w[0].toLocaleUpperCase("tr") + w.slice(1) : ""))
    .join(" ");

const normTR = (s = "") =>
  s
    .toString()
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ")
    .trim();

/* ── UI helpers (AdminPanel'den bağımsız, kendi içinde tanımlı) ── */
const cx = (...list) => list.filter(Boolean).join(" ");

function AInput({ value, onChange, placeholder, type = "text", disabled }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
        disabled:bg-slate-50 disabled:text-slate-400 placeholder:text-slate-400 transition"
    />
  );
}

function ASelect({ value, onChange, children, disabled }) {
  return (
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition
        appearance-none bg-white disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
    >
      {children}
    </select>
  );
}

function ATextarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
        placeholder:text-slate-400 transition resize-none"
    />
  );
}

function Field({ label, children, span2 }) {
  return (
    <div className={cx(span2 && "md:col-span-2")}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
        </label>
      )}
      {children}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div className="flex items-center gap-2 pt-2 pb-1">
      <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
        {children}
      </span>
      <div className="flex-1 h-px bg-slate-100" />
    </div>
  );
}

/* ── Firebase firma → form shape mapper ── */
function fbToForm(id, d) {
  return {
    _source: "firebase",
    _fbId: id,
    firmaAdi: d.firmaAdi || "",
    telefon: d.telefon || "",
    fax: d.fax || "",
    gsm: d.gsm || "",
    email: d.email || "",
    web: d.web || "",
    sosyalMedya: d.sosyalMedya || "",
    tanitimVideo: d.tanitimVideo || "",
    logoUrl: d.logoUrl || "",
    logoFile: null,
    logoPreviewUrl: d.logoUrl || "",
    kurulusYili: d.kurulusYili || "",
    isletmeTuru: d.isletmeTuru || "",
    yetkiliKisi: d.yetkiliKisi || "",
    yetkiliPozisyon: d.yetkiliPozisyon || "",
    calismaSaatleri: d.calismaSaatleri || "",
    anahtarKelimeler: d.anahtarKelimeler || [],
    anahtarKelimeInput: "",
    hakkinda: d.hakkinda || "",
    ulke: d.ulke || "Türkiye",
    sehir: d.sehir || "",
    ilce: d.ilce || "",
    sivasIlce: d.sivasIlce || "",
    sivasKoy: d.sivasKoy || "",
    acikAdres: d.acikAdres || "",
    secilenSektorler: d.secilenSektorler || [],
    sektorInput: "",
    altSektorler: d.altSektorler || [],
    altSektorInput: "",
    isGold: d.isGold || false,
    isVitrin: d.isVitrin || false,
    urunler: d.urunler || [],
  };
}

export default function FirmEditTab({ loading, setLoading, showStatus }) {
  /* ── Veri ── */
  const [fbFirmalar, setFbFirmalar] = useState([]);
  const [fbLoading, setFbLoading] = useState(true);
  const [country, setCountry] = React.useState("Türkiye");
  const [city, setCity] = React.useState("");
  const [cityDistrict, setCityDistrict] = React.useState("");
  const toSentenceCase = (s = "") =>
    s.charAt(0).toLocaleUpperCase("tr") + s.slice(1).toLocaleLowerCase("tr");

  useEffect(() => {
    const q = query(collection(db, "firmalar"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setFbFirmalar(
          snap.docs.map((d) => ({
            id: d.id,
            _source: "firebase",
            firmaAdi: d.data().firmaAdi || "",
            sehir: d.data().sehir || "",
            ilce: d.data().ilce || "",
            logoUrl: d.data().logoUrl || "",
            isGold: d.data().isGold || false,
            isVitrin: d.data().isVitrin || false,
            secilenSektorler: d.data().secilenSektorler || [],
            altSektorler: d.data().altSektorler || [],
            _raw: d.data(),
          }))
        );
        setFbLoading(false);
      },
      () => setFbLoading(false)
    );
    return () => unsub();
  }, []);

  const allFirms = fbFirmalar;

  /* ── Arama & seçim ── */
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [products, setProducts] = useState([]);

  // Türkiye seçili mi?
  const isTurkiye = country === "Türkiye";

  // Seçilen ile göre ilçeleri al (il-ilceler.json'dan) - SADECE TÜRKİYE İÇİN
  const cityDistricts = React.useMemo(() => {
    if (!city || !isTurkiye) return [];
    const ilData = ilIlceData.find((il) => normTR(il.text) === normTR(city));
    if (!ilData) return [];
    return ilData.districts
      .map((d) => d.text)
      .sort((a, b) => a.localeCompare(b, "tr"));
  }, [city, isTurkiye]);

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return allFirms;
    return allFirms.filter((f) =>
      `${f.firmaAdi} ${f.sehir} ${f.ilce} ${(f.secilenSektorler || []).join(
        " "
      )}`
        .toLocaleLowerCase("tr")
        .includes(q)
    );
  }, [allFirms, search]);

  /* ── Form ── */
  const set = (patch) => setSelected((p) => ({ ...p, ...patch }));
  const fileRef = useRef(null);

  const ilceler = useMemo(
    () =>
      [...new Set(ilceKoyler.map((x) => x.ilce))].sort((a, b) =>
        a.localeCompare(b, "tr")
      ),
    []
  );
  const koyler = useMemo(
    () =>
      selected?.sivasIlce
        ? ilceKoyler
            .filter((x) => x.ilce === selected.sivasIlce)
            .map((x) => x.koy)
            .sort()
        : [],
    [selected?.sivasIlce]
  );

  const handleSelectFirm = (firm) => {
    setSelected(fbToForm(firm.id, firm._raw));
    setProducts(firm._raw.urunler || []);
    setCountry(firm._raw.ulke || "Türkiye");
    setCity(firm._raw.sehir || "");
    setCityDistrict(firm._raw.ilce || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteFirm = async (e, firm) => {
    e.stopPropagation();
    if (!window.confirm(`"${firm.firmaAdi}" silinsin mi?`)) return;
    try {
      await deleteDoc(doc(db, "firmalar", firm.id));
      showStatus("success", "Firma silindi.");
    } catch (err) {
      showStatus("error", "Silinemedi: " + err.message);
    }
  };

  const handleLogoFile = (f) => {
    if (!f) return;
    if (selected.logoPreviewUrl && selected.logoPreviewUrl.startsWith("blob:"))
      URL.revokeObjectURL(selected.logoPreviewUrl);
    set({ logoFile: f, logoPreviewUrl: URL.createObjectURL(f), logoUrl: "" });
  };

  const removeLogo = () => {
    if (selected.logoPreviewUrl?.startsWith("blob:"))
      URL.revokeObjectURL(selected.logoPreviewUrl);
    set({ logoFile: null, logoPreviewUrl: "", logoUrl: "" });
    if (fileRef.current) fileRef.current.value = "";
  };

  /* ── Kaydet ── */
  const handleSave = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setLoading(true);
    try {
      /* Logo yükle */
      let finalLogoUrl = selected.logoUrl || "";
      if (selected.logoFile) {
        const user = auth.currentUser;
        const fname = `logo_${Date.now()}_${selected.logoFile.name}`;
        const sRef = ref(storage, `firmalar/${user?.uid}/${fname}`);
        await uploadBytes(sRef, selected.logoFile);
        finalLogoUrl = await getDownloadURL(sRef);
      }

      const uploadedProducts = await uploadAndPrepareProducts(products);
      const {
        _source,
        _fbId,
        _jsonId,
        _jsonIdx,
        logoFile,
        logoPreviewUrl,
        logoUrl,
        anahtarKelimeInput,
        sektorInput,
        altSektorInput,
        ...rest
      } = selected;

      const payload = {
        ...rest,
        logoUrl: finalLogoUrl,
        urunler: uploadedProducts,
      };

      if (_source === "firebase" && _fbId) {
        await updateDoc(doc(db, "firmalar", _fbId), {
          ...payload,
          updatedAt: serverTimestamp(),
        });
        showStatus("success", "Firma güncellendi.");
      } else {
        /* JSON firma → Firebase'e yeni kayıt olarak ekle */
        await addDoc(collection(db, "firmalar"), {
          ...payload,
          createdAt: serverTimestamp(),
          source: "admin",
        });
        showStatus("success", "Firma Firebase'e eklendi.");
      }
      setProducts([]);
      setSelected(null);
    } catch (err) {
      console.error(err);
      showStatus("error", "Hata: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ════ RENDER ════ */

  /* ── Düzenleme formu ── */
  if (selected)
    return (
      <div>
        {/* Geri */}
        <button
          type="button"
          onClick={() => setSelected(null)}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-5 transition"
        >
          <ChevronLeft className="w-4 h-4" /> Listeye Dön
        </button>

        <div className="flex items-center gap-2 mb-4">
          <Edit3 className="w-4 h-4 text-amber-500" />
          <h3 className="font-semibold text-slate-900 text-sm">
            {selected._source === "firebase"
              ? "Firebase Firması Düzenle"
              : "JSON Firmasını Firebase'e Aktar & Düzenle"}
          </h3>
          {selected._source === "json" && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">
              JSON → Firebase
            </span>
          )}
        </div>

        <form onSubmit={handleSave} className="space-y-5">
          {/* ── Logo ── */}
          <SectionHeading>Logo</SectionHeading>
          <div className="flex items-start gap-4">
            {/* Önizleme */}
            <div className="relative w-20 h-20 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center hover:border-slate-400 transition cursor-pointer group shrink-0">
              {selected.logoPreviewUrl ? (
                <img
                  src={selected.logoPreviewUrl}
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              ) : (
                <span className="text-xs text-slate-400 text-center px-1">
                  Logo Yükle
                </span>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="absolute inset-0 opacity-0 cursor-pointer"
                onChange={(e) => handleLogoFile(e.target.files?.[0])}
              />
            </div>

            <div className="flex-1 space-y-2">
              {/* Logo URL */}
              <Field label="Logo URL">
                <AInput
                  value={selected.logoUrl}
                  onChange={(e) => {
                    set({
                      logoUrl: e.target.value,
                      logoPreviewUrl: e.target.value,
                      logoFile: null,
                    });
                    if (fileRef.current) fileRef.current.value = "";
                  }}
                  placeholder="https://…"
                  disabled={!!selected.logoFile}
                />
              </Field>
              {selected.logoPreviewUrl && (
                <button
                  type="button"
                  onClick={removeLogo}
                  className="text-xs text-red-500 hover:text-red-700 transition"
                >
                  Logoyu Kaldır
                </button>
              )}
              <p className="text-[11px] text-slate-400">
                URL girin veya yukarıdaki kutuya tıklayarak dosya yükleyin.
              </p>
            </div>
          </div>

          {/* ── Temel Bilgiler ── */}
          <SectionHeading>Temel Bilgiler</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Firma Adı *" span2>
              <AInput
                value={selected.firmaAdi}
                onChange={(e) => set({ firmaAdi: e.target.value })}
                placeholder="Firma Adı"
              />
            </Field>
            <Field label="E-posta">
              <AInput
                value={selected.email}
                onChange={(e) => set({ email: e.target.value })}
                placeholder="ornek@email.com"
              />
            </Field>
            <Field label="Web Sitesi">
              <AInput
                value={selected.web}
                onChange={(e) => set({ web: e.target.value })}
                placeholder="https://…"
              />
            </Field>
          </div>

          {/* ── İletişim ── */}
          <SectionHeading>İletişim</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Telefon">
              <AInput
                value={selected.telefon}
                onChange={(e) => set({ telefon: e.target.value })}
                placeholder="0 (212) 000 00 00"
              />
            </Field>
            <Field label="Faks">
              <AInput
                value={selected.fax}
                onChange={(e) => set({ fax: e.target.value })}
                placeholder="0 (212) 000 00 00"
              />
            </Field>
            <Field label="GSM">
              <AInput
                value={selected.gsm}
                onChange={(e) => set({ gsm: e.target.value })}
                placeholder="05XX XXX XX XX"
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Sosyal Medya">
              <AInput
                value={selected.sosyalMedya}
                onChange={(e) => set({ sosyalMedya: e.target.value })}
                placeholder="https://instagram.com/…"
              />
            </Field>
            <Field label="Çalışma Saatleri">
              <AInput
                value={selected.calismaSaatleri}
                onChange={(e) => set({ calismaSaatleri: e.target.value })}
                placeholder="Hafta içi 09:00–18:00"
              />
            </Field>
          </div>

          {/* ── Yetkili ── */}
          <SectionHeading>Yetkili Kişi</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Kuruluş Yılı">
              <AInput
                type="number"
                value={selected.kurulusYili}
                onChange={(e) => set({ kurulusYili: e.target.value })}
                placeholder="2010"
              />
            </Field>
            <Field label="Yetkili Kişi">
              <AInput
                value={selected.yetkiliKisi}
                onChange={(e) => set({ yetkiliKisi: e.target.value })}
                placeholder="Ad Soyad"
              />
            </Field>
            <Field label="Yetkili Pozisyon">
              <AInput
                value={selected.yetkiliPozisyon}
                onChange={(e) => set({ yetkiliPozisyon: e.target.value })}
                placeholder="Genel Müdür"
              />
            </Field>
          </div>

          {/* ── Konum ── */}
          <SectionHeading>Konum</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Ülke">
              {/* Ülke Dropdown */}
              <div className="relative flex items-center bg-white rounded-xl shadow-lg h-14 md:h-16 px-4 md:px-5">
                <Globe className="text-gray-400 w-5 h-5 mr-3 shrink-0" />
                <div className="min-w-0 w-full relative">
                  <select
                    value={selected.ulke}
                    onChange={(e) => {
                      setCountry(e.target.value);
                      setCity("");
                      setCityDistrict("");
                      set({ ulke: e.target.value, sehir: "", ilce: "" });
                    }}
                    className="min-w-0 w-full outline-none text-gray-800 text-[14px] md:text-[16px] bg-transparent appearance-none cursor-pointer"
                  >
                    {COUNTRIES.map((ulke) => (
                      <option key={ulke} value={ulke}>
                        {ulke}
                      </option>
                    ))}
                  </select>
                </div>
                <ChevronDown className="text-gray-400 w-4 h-4 shrink-0 pointer-events-none" />
              </div>
            </Field>

            <Field label="Şehir / İl">
              <div className="relative flex items-center bg-white rounded-xl shadow-lg h-14 md:h-16 px-4 md:px-5">
                <MapPin className="text-gray-400 w-5 h-5 mr-3 shrink-0" />
                <div className="min-w-0 w-full relative">
                  {!city && (
                    <span className="absolute inset-0 flex items-center text-gray-400 text-[12px] md:text-[14px] pointer-events-none">
                      {isTurkiye ? "İl seçiniz..." : "Sadece Türkiye"}
                    </span>
                  )}
                  <select
                    value={selected.sehir}
                    onChange={(e) => {
                      setCity(e.target.value);
                      setCityDistrict("");
                      set({ sehir: e.target.value, ilce: "" });
                    }}
                    disabled={!isTurkiye}
                    className="min-w-0 w-full outline-none text-gray-800 text-[14px] md:text-[16px] bg-transparent appearance-none cursor-pointer disabled:cursor-not-allowed disabled:text-gray-400"
                  >
                    <option value=""></option>
                    {isTurkiye &&
                      TURKEY_CITIES.map((il) => (
                        <option key={il} value={il}>
                          {il}
                        </option>
                      ))}
                  </select>
                </div>
                <ChevronDown className="text-gray-400 w-4 h-4 shrink-0 pointer-events-none" />
              </div>
            </Field>
            <Field label="İlçe">
              <div className="relative flex items-center bg-white rounded-xl shadow-lg h-14 md:h-16 px-4 md:px-5">
                <ChevronDown className="text-gray-400 w-5 h-5 mr-3 shrink-0" />
                <div className="min-w-0 w-full relative">
                  {!cityDistrict && (
                    <span className="absolute inset-0 flex items-center text-gray-400 text-[12px] md:text-[14px] pointer-events-none">
                      {!isTurkiye
                        ? "Sadece Türkiye"
                        : city
                        ? ""
                        : "Önce il seçiniz..."}
                    </span>
                  )}
                  <select
                    value={selected.ilce}
                    onChange={(e) => {
                      setCityDistrict(e.target.value);
                      set({ ilce: e.target.value });
                    }}
                    disabled={!city || !isTurkiye}
                    className="min-w-0 w-full outline-none text-gray-800 text-[14px] md:text-[16px] bg-transparent appearance-none cursor-pointer disabled:cursor-not-allowed disabled:text-gray-400"
                  >
                    <option value=""></option>
                    {cityDistricts.map((ilce) => (
                      <option key={ilce} value={ilce}>
                        {toTitleTR(ilce)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Field>
          </div>
          <Field label="Açık Adres">
            <ATextarea
              value={selected.acikAdres}
              onChange={(e) => set({ acikAdres: e.target.value })}
              placeholder="Mahalle, cadde/sokak, bina, kapı no…"
              rows={2}
            />
          </Field>

          <p className="text-sm font-medium text-slate-700">
            Sivasın hangi ilçe / köyündensiniz?
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="İlçe (Sivas)">
              <ASelect
                value={selected.sivasIlce}
                onChange={(e) =>
                  set({ sivasIlce: e.target.value, sivasKoy: "" })
                }
              >
                <option value="">İlçe seçiniz…</option>
                {ilceler.map((il) => (
                  <option key={il} value={il}>
                    {il}
                  </option>
                ))}
              </ASelect>
            </Field>
            <Field label="Köy / Mahalle">
              <ASelect
                value={selected.sivasKoy}
                onChange={(e) => set({ sivasKoy: e.target.value })}
                disabled={!selected.sivasIlce}
              >
                <option value="">
                  {selected.sivasIlce ? "Köy seçiniz…" : "Önce ilçe seçiniz"}
                </option>
                {koyler.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </ASelect>
            </Field>
          </div>

          

          <Field label="Tanıtım Filmi">
            <ATextarea
              value={selected.tanitimVideo}
              onChange={(e) => set({ tanitimVideo: e.target.value })}
              placeholder="Youtube tanıtım video linki…"
              rows={2}
            />
          </Field>

          {/* ── Sektörler ── */}
          <SectionHeading>Sektörler</SectionHeading>
          <ASelect
            value=""
            onChange={(e) => {
              if (!e.target.value) return;
              const v = e.target.value;
              if (selected.secilenSektorler.includes(v)) return;
              set({ secilenSektorler: [...selected.secilenSektorler, v] });
            }}
          >
            <option value="">Ana sektör seçin…</option>
            {ANA_SEKTORLER.map((s) => (
              <option key={s} value={s}>
                {toSentenceCase(s)}
              </option>
            ))}
          </ASelect>
          <div className="flex gap-2 mt-2">
            <AInput
              value={selected.sektorInput}
              onChange={(e) => set({ sektorInput: e.target.value })}
              placeholder="Listede yoksa yazın…"
            />
            <button
              type="button"
              onClick={() => {
                const v = selected.sektorInput.trim();
                if (!v || selected.secilenSektorler.includes(v))
                  return set({ sektorInput: "" });
                set({
                  secilenSektorler: [...selected.secilenSektorler, v],
                  sektorInput: "",
                });
              }}
              className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Ekle
            </button>
          </div>
          {selected.secilenSektorler.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {selected.secilenSektorler.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-xs font-medium"
                >
                  {s}
                  <button
                    type="button"
                    onClick={() =>
                      set({
                        secilenSektorler: selected.secilenSektorler.filter(
                          (x) => x !== s
                        ),
                      })
                    }
                    className="text-amber-600 hover:text-red-600 transition"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* ── Alt Sektörler ── */}
          <SectionHeading>Alt Sektörler</SectionHeading>
          {(() => {
            const altSecenekler =
              selected.secilenSektorler.length > 0
                ? [
                    ...new Set(
                      selected.secilenSektorler.flatMap(
                        (s) => SEKTOR_DATA[s] || []
                      )
                    ),
                  ]
                : [];
            return (
              <ASelect
                value=""
                onChange={(e) => {
                  if (!e.target.value) return;
                  const v = e.target.value;
                  if (selected.altSektorler.includes(v)) return;
                  set({ altSektorler: [...selected.altSektorler, v] });
                }}
                disabled={altSecenekler.length === 0}
              >
                <option value="">
                  {selected.secilenSektorler.length > 0
                    ? "Alt sektör seçin…"
                    : "Önce ana sektör seçin"}
                </option>
                {altSecenekler.map((s) => (
                  <option key={s} value={s}>
                    {toSentenceCase(s)}
                  </option>
                ))}
              </ASelect>
            );
          })()}
          <div className="flex gap-2 mt-2">
            <AInput
              value={selected.altSektorInput}
              onChange={(e) => set({ altSektorInput: e.target.value })}
              placeholder="Listede yoksa yazın…"
            />
            <button
              type="button"
              onClick={() => {
                const v = selected.altSektorInput.trim();
                if (!v || selected.altSektorler.includes(v))
                  return set({ altSektorInput: "" });
                set({
                  altSektorler: [...selected.altSektorler, v],
                  altSektorInput: "",
                });
              }}
              className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Ekle
            </button>
          </div>
          {selected.altSektorler.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {selected.altSektorler.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-xs font-medium"
                >
                  {s}
                  <button
                    type="button"
                    onClick={() =>
                      set({
                        altSektorler: selected.altSektorler.filter(
                          (x) => x !== s
                        ),
                      })
                    }
                    className="text-amber-600 hover:text-red-600 transition"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* ── Anahtar Kelimeler ── */}
          <SectionHeading>Anahtar Kelimeler</SectionHeading>
          <div className="flex gap-2">
            <AInput
              value={selected.anahtarKelimeInput}
              onChange={(e) => set({ anahtarKelimeInput: e.target.value })}
              placeholder="Kelime ekle…"
            />
            <button
              type="button"
              onClick={() => {
                const k = selected.anahtarKelimeInput.trim();
                if (!k || selected.anahtarKelimeler.includes(k))
                  return set({ anahtarKelimeInput: "" });
                set({
                  anahtarKelimeler: [...selected.anahtarKelimeler, k],
                  anahtarKelimeInput: "",
                });
              }}
              className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Ekle
            </button>
          </div>
          {selected.anahtarKelimeler.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {selected.anahtarKelimeler.map((k) => (
                <span
                  key={k}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-700 border border-slate-200 rounded-full text-xs"
                >
                  {k}
                  <button
                    type="button"
                    onClick={() =>
                      set({
                        anahtarKelimeler: selected.anahtarKelimeler.filter(
                          (x) => x !== k
                        ),
                      })
                    }
                    className="text-slate-400 hover:text-red-500 transition"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* ── Hakkında ── */}
          <SectionHeading>Firma Hakkında</SectionHeading>
          <ATextarea
            value={selected.hakkinda}
            onChange={(e) => set({ hakkinda: e.target.value })}
            placeholder="Firmanızı anlatın…"
            rows={4}
          />

          {/* ── Üyelik ── */}
          <SectionHeading>Üyelik Tipi</SectionHeading>
          <div className="flex flex-wrap gap-4">
            {[
              {
                key: "isGold",
                label: "Gold Firma",
                color: "bg-amber-400",
                icon: "⭐",
              },
              {
                key: "isVitrin",
                label: "Vitrin Firma",
                color: "bg-sky-500",
                icon: "🏪",
              },
            ].map(({ key, label, color, icon }) => (
              <label
                key={key}
                className="flex items-center gap-3 cursor-pointer w-fit"
              >
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={selected[key]}
                    onChange={(e) => set({ [key]: e.target.checked })}
                    className="sr-only"
                  />
                  <div
                    className={cx(
                      "w-11 h-6 rounded-full transition-colors duration-200",
                      selected[key] ? color : "bg-slate-200"
                    )}
                  >
                    <div
                      className={cx(
                        "absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200",
                        selected[key] ? "translate-x-5" : "translate-x-0"
                      )}
                    />
                  </div>
                </div>
                <span className="text-sm font-medium text-slate-700">
                  {selected[key] && <span className="mr-1">{icon}</span>}
                  {label}
                </span>
              </label>
            ))}
          </div>
          {/* ── Ürünler ── */}
          <SectionHeading>Ürünler & Hizmetler</SectionHeading>
          <ProductsSection products={products} setProducts={setProducts} />

          {/* ── Submit ── */}
          <div className="pt-2 flex gap-3">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {loading
                ? "Kaydediliyor…"
                : selected._source === "firebase"
                ? "Güncelle"
                : "Firebase'e Aktar & Kaydet"}
            </button>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="px-4 py-2.5 rounded-xl text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              İptal
            </button>
          </div>
        </form>
      </div>
    );

  /* ── Liste görünümü ── */
  return (
    <div>
      {/* Arama */}
      <div className="relative mb-4">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Firma adı, şehir veya sektör ara…"
          className="w-full pl-9 pr-9 py-2.5 text-sm border border-slate-200 rounded-xl
            focus:outline-none focus:ring-2 focus:ring-slate-900 bg-slate-50 placeholder:text-slate-400"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Sayaç */}
      <p className="text-xs text-slate-400 mb-3">
        <span className="font-medium text-slate-600">{filtered.length}</span>{" "}
        firma listeleniyor
      </p>

      {fbLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-14 bg-slate-100 rounded-xl animate-pulse"
            />
          ))}
        </div>
      )}

      {!fbLoading && (
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
          {filtered.map((firm) => (
            <button
              key={firm.id}
              type="button"
              onClick={() => handleSelectFirm(firm)}
              className="w-full flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200
                hover:border-slate-300 hover:shadow-sm transition text-left group"
            >
              {/* Logo */}
              <div className="w-10 h-10 rounded-lg border border-slate-100 bg-slate-50 overflow-hidden shrink-0 flex items-center justify-center">
                {firm.logoUrl ? (
                  <img
                    src={firm.logoUrl}
                    alt={firm.firmaAdi}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <Building2 className="w-4 h-4 text-slate-300" />
                )}
              </div>

              {/* Bilgi */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-sm text-slate-900 truncate">
                    {firm.firmaAdi || "—"}
                  </span>
                  {firm.isGold && (
                    <Star className="w-3 h-3 text-amber-500 shrink-0" />
                  )}
                </div>
                <div className="text-xs text-slate-400 truncate">
                  {[firm.sehir, firm.ilce].filter(Boolean).join(" · ")}
                  {firm.secilenSektorler?.[0] &&
                    ` · ${firm.secilenSektorler[0]}`}
                </div>
              </div>

              {/* Kaynak badge */}
              <span
                className={cx(
                  "text-[10px] px-1.5 py-0.5 rounded-full border font-medium shrink-0",
                  firm._source === "firebase"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"
                )}
              >
                <button
                  type="button"
                  onClick={(e) => handleDeleteFirm(e, firm)}
                  className="shrink-0 p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition"
                  title="Sil"
                >
                  <X className="w-4 h-4" />
                </button>
              </span>

              <Edit3 className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 shrink-0 transition" />
            </button>
          ))}

          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <Building2 className="w-8 h-8 mx-auto mb-2 text-slate-200" />
              <p className="text-sm">Firma bulunamadı.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
